/**
 * Kassie Presenter Mode
 * Used on the device activation screen. The loader slides to the KassieAvatar position,
 * replaces it, then runs a welcome script followed by idle entertainment sequences.
 *
 * Public API:
 *   initPresenterMode() — detect activation screen, slide loader, start presenting
 */

import { t } from '../i18n/config.js';
import { KassieCharacter } from './kassieCharacter.js';

// ═══════════════════════════════════════════════════════════════
// STATE
// ═══════════════════════════════════════════════════════════════

let character = null;
let containerEl = null;
let bubbleEl = null;
let bubbleTextEl = null;
let initialized = false;
let presenting = false;

// ═══════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════

/**
 * Initialize presenter mode on the activation screen.
 * Checks if device is not yet activated, finds KassieAvatar.svg,
 * slides the character to that position, then starts the welcome script.
 */
export async function initPresenterMode() {
  // Only run on activation screen (device not authorized)
  const deviceAuth = localStorage.getItem('deviceAuthorization');
  if (deviceAuth) return; // Device already activated, skip presenter

  // Wait for the loader to hide first (page done loading)
  await waitForLoaderHidden();

  // Find the KassieAvatar on the activation page
  const avatarImg = document.querySelector('.activation-icon img[src*="KassieAvatar"]');
  if (!avatarImg) return; // Not on activation screen

  // Get the activation-icon container to place presenter inside it
  const activationIcon = document.querySelector('.activation-icon');
  if (!activationIcon) return;

  // Create the presenter container inside activation-icon
  // 180px to match the boot loader's own size exactly (kassie-loader-v2.css
  // .kassie-loader-v2 { width: 180px; height: 180px }) — the loader shrinks
  // toward this exact spot before the presenter appears (see
  // kassieLoaderV3.js's hideLoader()/getHideTargetCenter()), so the two
  // must be the same physical size for that handoff to look continuous
  // rather than a size jump.
  containerEl = document.createElement('div');
  containerEl.className = 'kassie-presenter';
  containerEl.style.cssText = `
    width: 180px;
    height: 180px;
    margin: 0 auto;
    overflow: visible;
  `;

  // Hide the original avatar and insert the presenter in its place.
  //
  // The whole .activation-avatar-container has to go, not just the <img>: it is
  // a bordered circle with a fixed 140px size, so hiding only the image left an
  // empty ringed circle sitting above Kassie.
  //
  // The presenter is appended as a sibling rather than dropped inside that
  // container on purpose — the container is overflow: hidden, and Kassie waves
  // an arm outside her own bounds, which would be clipped in there.
  const avatarFrame = avatarImg.closest('.activation-avatar-container') || avatarImg;
  avatarFrame.style.display = 'none';
  activationIcon.appendChild(containerEl);

  // Initialize the character engine
  character = new KassieCharacter();
  await character.init(containerEl, { size: '100%' });

  // Create speech bubble
  bubbleEl = document.createElement('div');
  bubbleEl.className = 'kassie-loader-v2__bubble kassie-loader-v2__bubble--bottom';
  bubbleEl.innerHTML = `<div class="kassie-loader-v2__bubble-tail"></div><span class="kassie-loader-v2__bubble-text"></span>`;
  bubbleEl.style.display = 'none';
  document.body.appendChild(bubbleEl);
  bubbleTextEl = bubbleEl.querySelector('.kassie-loader-v2__bubble-text');

  // Position bubble relative to presenter
  positionBubble();

  // Set up mouse tracking for eyes
  document.addEventListener('mousemove', handlePresenterMouseMove);

  // Keep the speech bubble locked to the presenter's on-screen position
  // whenever the page scrolls. The bubble is position: fixed (viewport
  // coordinates), and positionBubble() only ever recalculated those
  // coordinates on specific triggers (a new line appearing, a state-panel
  // switch) — nothing tracked ordinary page scroll, so once the page grew
  // tall enough to scroll, the presenter moved with the content underneath
  // it while the bubble stayed wherever it was last positioned, visibly
  // separating the two. window, not document, and capture: true, so this
  // also catches scrolling on an inner scrollable container, not just the
  // document body.
  window.addEventListener('scroll', handlePresenterScroll, {
    passive: true,
    capture: true,
  });

  // Start look-around timer (for mobile / no mouse activity)
  resetPresenterLookAround();

  initialized = true;
  presenting = true;

  // Random ear-wiggle and single-eye-wink idle cycles, independent of the
  // speech loop below — keeps the character feeling alive even while a
  // line is holding on screen for a few seconds.
  startEarWiggleCycle();
  startWinkCycle();

  // Start blinking
  character.startBlinking();

  // Run welcome script then idle loop
  await runWelcomeScript();
  await runPresenterIdleLoop();
}

// ═══════════════════════════════════════════════════════════════
// SPEECH LINES — one single, sequential, looping script
// ═══════════════════════════════════════════════════════════════

/**
 * Every line the presenter speaks, always in this exact order, looping
 * forever. Read lazily (functions, not plain strings) so the copy follows a
 * language change made mid-conversation.
 *
 * Named indices below (SCAN_QR_INDEX, ENTER_CODE_INDEX) let hovering over
 * the "Scan QR code" / "Use a code" buttons jump the loop back to the
 * matching line instead of waiting for it to come around naturally — see
 * restartLinesFrom() and DeviceActivation.js's hover bindings.
 */
const LINES = [
  () => t('child.presenter.welcomeToKassie') || 'Welkom by Kassie',
  () => t('child.presenter.scanQrCode') || 'Skandeer QR kode!',
  () => t('child.presenter.enterCode') || "Tik 'n kode in",
  () => t('child.presenter.askAdult') || "Vra 'n grootmens om te help",
  () => t('child.presenter.letsSing') || 'Dan kan ons sing, speel en lees',
  () => t('child.presenter.gladYouAreHere') || 'Ek is bly jy is hier',
  () => t('child.presenter.youAreAStar') || "Jy is 'n ster!",
  () => t('child.presenter.chooseLanguage') || 'Hier kan jy jou taal kies',
];

const SCAN_QR_INDEX = 1; // 'scanQrCode'
const ENTER_CODE_INDEX = 2; // 'enterCode'
const ASK_ADULT_INDEX = 3; // 'askAdult'
const GLAD_YOU_ARE_HERE_INDEX = 5; // 'gladYouAreHere'
const YOU_ARE_A_STAR_INDEX = 6; // 'youAreAStar'

/** Lines that get a random one-off gesture (wink, eyebrow waggle, or ear wiggle). */
const RANDOM_GESTURE_LINE_INDICES = [ASK_ADULT_INDEX, GLAD_YOU_ARE_HERE_INDEX, YOU_ARE_A_STAR_INDEX];

/** How long each line stays up (matches the old idle-loop pacing). */
const LINE_MIN_MS = 2000;
const LINE_MAX_MS = 4000;

/** Index of the next line to speak. Wraps to 0 at the end of LINES. */
let lineIndex = 0;

/**
 * Jump the speech loop to a specific line, so it speaks that one next
 * instead of wherever it currently is.
 *
 * Only records the intent — the currently-speaking line is allowed to
 * finish naturally (cutting it off mid-word would look broken); the loop
 * picks up the new index on its next iteration. Safe to call even if the
 * presenter isn't active (e.g. device already authorized).
 * @param {number} index
 */
function restartLinesFrom(index) {
  lineIndex = index;
}

/** Restart the speech loop from "Skandeer QR-kode" — call on Scan QR button hover. */
export function restartFromScanQr() {
  restartLinesFrom(SCAN_QR_INDEX);
}

/** Restart the speech loop from "Tik 'n kode in" — call on Use a code button hover. */
export function restartFromEnterCode() {
  restartLinesFrom(ENTER_CODE_INDEX);
}

/**
 * One-off lines queued to override whatever LINES[lineIndex] would have
 * spoken next, consumed one at a time by the loop in
 * runPresenterIdleLoop() the next time(s) it iterates. Not spoken directly
 * here: the loop may currently be mid-way through its own
 * `await character.speak(holdMs)` for a different line, and
 * character.speak()/stopSpeaking() have no safe way to interrupt an
 * in-flight call without either running two overlapping mouth-flap loops or
 * permanently hanging that pending await. Queuing overrides and updating
 * the visible bubble text immediately (below, for the first one) gives
 * instant visual feedback without touching the character engine's own
 * timers at all.
 */
let overrideQueue = [];
const OVERRIDE_HOLD_MS = 3200;

/**
 * Speak the code-entry instructions, followed by where to find the code —
 * call when the "Use a code"/"Gebruik 'n kode" button is actually clicked
 * (distinct from restartFromEnterCode(), which only jumps the loop's
 * hover-preview line to the generic enterCode line; this pair is the
 * explicit instructions for the screen the click just revealed). The
 * bubble text changes immediately for the first line; the character's own
 * mouth-talk timing picks both lines up properly, one per loop iteration.
 * Resumes the normal loop from ENTER_CODE_INDEX afterward.
 */
export function speakEnterCodeInstructions() {
  if (!character || !presenting) return;
  const line1 =
    t('child.presenter.enterCodeInstructions') ||
    "Tik die kode in wat 'n grootmens vir jou gegee het en druk aktiveer";
  const line2 =
    t('child.presenter.getCodeInAdminApp') ||
    'Kry die kode in die Admin-toep → Gesin → Gemagtigde Toestelle';
  // alreadyShown on the first item only — its bubble text was just set
  // synchronously below for instant feedback, so the loop must not
  // re-trigger setBubbleText()/positionBubble() for it a second time.
  overrideQueue = [
    { text: line1, alreadyShown: true },
    { text: line2, alreadyShown: false },
  ];
  setBubbleText(line1);
  restartLinesFrom(ENTER_CODE_INDEX);
}

/**
 * Speak the "where to find the code" line, then the two camera-permission
 * reminders — call when the "Scan QR code"/"Skandeer QR-kode" button is
 * actually clicked (distinct from restartFromScanQr(), which only jumps
 * the loop's hover-preview line to the generic scanQrCode line; this trio
 * is the explicit instructions for the screen the click just revealed).
 * Resumes the normal loop from SCAN_QR_INDEX afterward.
 */
export function speakScanQrInstructions() {
  if (!character || !presenting) return;
  const line1 =
    t('child.presenter.getCodeInAdminApp') ||
    'Kry die kode in die Admin-toep → Gesin → Gemagtigde Toestelle';
  const line2 =
    t('child.presenter.rememberCameraNeeded') ||
    'Onthou kamera toegang is nodig om die QR-kode te lees';
  const line3 =
    t('child.presenter.goToSettingsForCamera') ||
    'Gaan na jou instellings om kamera-toegang toe te laat, of gebruik die kode';
  overrideQueue = [
    { text: line1, alreadyShown: true },
    { text: line2, alreadyShown: false },
    { text: line3, alreadyShown: false },
  ];
  setBubbleText(line1);
  restartLinesFrom(SCAN_QR_INDEX);
}

const WELCOME_INDEX = 0; // 'welcomeToKassie'

/**
 * Speak the welcome line with its arm gesture — both arms out, forearms
 * rotating up, a welcoming pose held briefly, then returned. Runs both for
 * the very first greeting (via runWelcomeScript()) and every time the
 * speech loop wraps back around to "Welkom by Kassie" on subsequent laps
 * (via runPresenterIdleLoop()), so the gesture repeats each time those
 * exact words are shown, not just once per page load.
 */
async function speakWelcomeWithGesture() {
  if (!character || !presenting) return;
  character.setExpression('happy');
  const welcomeText = LINES[WELCOME_INDEX]();
  setBubbleText(welcomeText);
  character.speak(welcomeText, 2000);
  // Wink with the right eye while saying "Welkom by Kassie" — a quick,
  // friendly aside alongside the arm gesture, not instead of it. Not
  // awaited: wink()'s own setTimeout duration (CONFIG.blinkDuration * 2)
  // is short relative to welcome()'s multi-second pose, so it plays out
  // naturally in the background rather than delaying the arms.
  character.wink('right');
  await character.welcome();
  character.setExpression('neutral');
}

async function runWelcomeScript() {
  if (!character || !presenting) return;
  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  await speakWelcomeWithGesture();
  lineIndex = 1;
  await delay(300);
}

// ═══════════════════════════════════════════════════════════════
// SPEECH LOOP (runs forever after the one-off welcome gesture above)
// ═══════════════════════════════════════════════════════════════

async function runPresenterIdleLoop() {
  if (!character || !presenting) return;
  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  // One line every 2-4 seconds, forever, in LINES order (wrapping), unless
  // restartLinesFrom() has just jumped lineIndex elsewhere. No arm gestures
  // here: the greeting is the only place arms appear. Expression and mouth
  // still animate, and the eyes keep tracking, so she is not static.
  while (presenting) {
    try {
      let holdMs;
      let text;
      if (overrideQueue.length > 0) {
        const next = overrideQueue.shift();
        if (!next.alreadyShown) setBubbleText(next.text);
        holdMs = OVERRIDE_HOLD_MS;
        text = next.text;
      } else if (lineIndex === WELCOME_INDEX) {
        // The loop wrapped back around to "Welkom by Kassie" — repeat the
        // arm-welcome gesture here too, not just on the very first greeting.
        lineIndex = (lineIndex + 1) % LINES.length;
        await speakWelcomeWithGesture();
        if (!presenting) return;
        continue;
      } else {
        const currentLineIndex = lineIndex;
        text = LINES[currentLineIndex]();
        setBubbleText(text);
        // Eyebrow flash/waggle while saying "Skandeer QR-kode!" — the
        // social "eyebrow flash" gesture (a quick raise-lower-raise-lower),
        // not awaited for the same reason wink() isn't in
        // speakWelcomeWithGesture(): it's a short aside that plays out in
        // the background of the longer speak() call below, not something
        // that should delay it.
        if (currentLineIndex === SCAN_QR_INDEX) character.eyebrowWaggle();
        // On these three lines, play exactly one randomly-chosen gesture
        // (wink, eyebrow waggle, or ear wiggle) instead of a fixed one —
        // variety across repeat laps of the loop rather than the same
        // gesture every single time.
        if (RANDOM_GESTURE_LINE_INDICES.includes(currentLineIndex)) {
          playRandomGesture();
        }
        lineIndex = (lineIndex + 1) % LINES.length;
        holdMs = LINE_MIN_MS + Math.random() * (LINE_MAX_MS - LINE_MIN_MS);
      }
      await character.speak(text, holdMs);
      if (!presenting) return;
    } catch (e) {
      console.warn('Presenter idle error:', e);
      await delay(1000);
    }
  }
}

/**
 * Play exactly one randomly-picked gesture — a wink (random eye), an
 * eyebrow waggle, or an ear wiggle (random ear) — used on askAdult,
 * gladYouAreHere, and youAreAStar (see RANDOM_GESTURE_LINE_INDICES), so
 * those three lines each get some variety rather than a fixed always-the-
 * same-gesture tell. Not awaited by callers: each of the three underlying
 * gestures is a short aside meant to play out in the background of the
 * longer character.speak() call, same as the wink in
 * speakWelcomeWithGesture() and the eyebrowWaggle() on the QR line.
 */
function playRandomGesture() {
  if (!character) return;
  const gestures = [
    () => character.wink(Math.random() < 0.5 ? 'left' : 'right'),
    () => character.eyebrowWaggle(),
    () => character.earWiggle(),
  ];
  const pick = gestures[Math.floor(Math.random() * gestures.length)];
  pick();
}


// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

let lastPresenterMouseUpdate = 0;
let presenterLookAroundTimer = null;

function resetPresenterLookAround() {
  clearTimeout(presenterLookAroundTimer);
  presenterLookAroundTimer = setTimeout(async () => {
    if (!presenting || !character) return;
    await character.lookAround();
    resetPresenterLookAround();
  }, 5000 + Math.random() * 3000);
}

function handlePresenterMouseMove(e) {
  const now = Date.now();
  if (now - lastPresenterMouseUpdate < 50) return;
  lastPresenterMouseUpdate = now;
  if (!presenting || !character) return;
  resetPresenterLookAround();
  character.lookAt(e.clientX, e.clientY);

  // Track circular motion for dizzy easter egg
  trackPresenterCircularMotion(e.clientX, e.clientY);
}

// ─── Presenter Dizzy Easter Egg ──────────────────────────────────────────────

let presenterCircleAngle = 0;
let presenterLastAngle = null;
let presenterDizzyActive = false;
let presenterDizzyTimeout = null;
const PRESENTER_CIRCLES_NEEDED = 3;
const PRESENTER_FULL_CIRCLE = Math.PI * 2;

function trackPresenterCircularMotion(mx, my) {
  if (presenterDizzyActive || !character || !containerEl) return;

  const rect = containerEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const angle = Math.atan2(my - cy, mx - cx);

  if (presenterLastAngle !== null) {
    let delta = angle - presenterLastAngle;
    if (delta > Math.PI) delta -= PRESENTER_FULL_CIRCLE;
    if (delta < -Math.PI) delta += PRESENTER_FULL_CIRCLE;
    presenterCircleAngle += delta;
  }
  presenterLastAngle = angle;

  if (Math.abs(presenterCircleAngle) >= PRESENTER_CIRCLES_NEEDED * PRESENTER_FULL_CIRCLE) {
    presenterCircleAngle = 0;
    presenterLastAngle = null;
    triggerPresenterDizzy();
  }
}

function triggerPresenterDizzy() {
  if (!character || presenterDizzyActive) return;
  presenterDizzyActive = true;

  // Show dizzy stars above the avatar
  import('./dizzyStars.js').then(({ showDizzyStars }) => {
    if (containerEl) showDizzyStars(containerEl);
  });

  // Hide pupils, show dizzy eyes (keep eye whites)
  character._hideLayer('left-pupil');
  character._hideLayer('right-pupil');
  character._showLayer('left-eye-dizzy');
  character._showLayer('right-eye-dizzy');

  // Ooh mouth
  character.setMouth('ooh');

  // Rotate dizzy layers around their own centers
  const leftDizzy = character.layers['left-eye-dizzy'];
  const rightDizzy = character.layers['right-eye-dizzy'];
  if (leftDizzy) {
    leftDizzy.style.transformBox = 'fill-box';
    leftDizzy.style.transformOrigin = 'center center';
    leftDizzy.style.transition = 'transform 1.5s linear';
    leftDizzy.style.transform = 'rotate(720deg)';
  }
  if (rightDizzy) {
    rightDizzy.style.transformBox = 'fill-box';
    rightDizzy.style.transformOrigin = 'center center';
    rightDizzy.style.transition = 'transform 1.5s linear';
    rightDizzy.style.transform = 'rotate(-720deg)';
  }

  presenterDizzyTimeout = setTimeout(() => {
    endPresenterDizzy();
  }, 1500);
}

function endPresenterDizzy() {
  if (!character) return;
  presenterDizzyActive = false;
  clearTimeout(presenterDizzyTimeout);
  presenterDizzyTimeout = null;

  // Hide dizzy stars
  import('./dizzyStars.js').then(({ hideDizzyStars }) => hideDizzyStars());

  character._hideLayer('left-eye-dizzy');
  character._hideLayer('right-eye-dizzy');
  character._showLayer('left-pupil');
  character._showLayer('right-pupil');
  character.setMouth('BigSmile');

  const leftDizzy = character.layers?.['left-eye-dizzy'];
  const rightDizzy = character.layers?.['right-eye-dizzy'];
  if (leftDizzy) { leftDizzy.style.transition = ''; leftDizzy.style.transform = ''; }
  if (rightDizzy) { rightDizzy.style.transition = ''; rightDizzy.style.transform = ''; }

  presenterCircleAngle = 0;
  presenterLastAngle = null;
}

let earWiggleTimer = null;
let winkTimer = null;

/**
 * Random ear-tickle/wiggle idle cycle — fires character.earWiggle() on a
 * random ear every 2-5 seconds for as long as the presenter is active.
 * Mirrors resetPresenterLookAround()'s recursive-setTimeout pattern (each
 * firing schedules the next one with a fresh random delay) rather than a
 * plain setInterval, so the interval itself varies each time instead of
 * being a fixed cadence.
 */
function startEarWiggleCycle() {
  clearTimeout(earWiggleTimer);
  earWiggleTimer = setTimeout(async () => {
    if (!presenting || !character) return;
    await character.earWiggle();
    startEarWiggleCycle();
  }, 2000 + Math.random() * 3000);
}

/**
 * Random single-eye wink idle cycle — winks a random eye (sometimes left,
 * sometimes right) every 2-5 seconds for as long as the presenter is
 * active. Independent timer from startEarWiggleCycle() rather than
 * sharing one, so the two gestures fire on their own random schedules and
 * don't always land at the same moment.
 */
function startWinkCycle() {
  clearTimeout(winkTimer);
  winkTimer = setTimeout(() => {
    if (!presenting || !character) return;
    character.wink(Math.random() < 0.5 ? 'left' : 'right');
    startWinkCycle();
  }, 8000 + Math.random() * 7000);
}

let scrollRepositionFrame = null;

/**
 * Re-run positionBubble() on scroll, so the bubble (position: fixed,
 * viewport coordinates) never lags behind the presenter (in normal page
 * flow, moves with scroll) even by a single frame. Coalesced onto
 * requestAnimationFrame rather than firing on every scroll event directly —
 * scroll fires far more often than the display can actually repaint, so
 * this keeps the work to at most once per frame without any visible delay.
 */
function handlePresenterScroll() {
  if (scrollRepositionFrame !== null) return;
  scrollRepositionFrame = requestAnimationFrame(() => {
    scrollRepositionFrame = null;
    if (bubbleEl && bubbleEl.style.display !== 'none') {
      positionBubble();
    }
  });
}

/**
 * Wait until the loader backdrop is no longer active (page finished loading).
 */
function waitForLoaderHidden() {
  return new Promise(resolve => {
    const check = () => {
      const backdrop = document.querySelector('.kassie-loader-v2__backdrop');
      if (!backdrop || !backdrop.classList.contains('active')) {
        resolve();
      } else {
        setTimeout(check, 200);
      }
    };
    // Give a small initial delay for the loader to appear first
    setTimeout(check, 500);
  });
}

function setBubbleText(text) {
  if (bubbleTextEl) bubbleTextEl.textContent = text;
  if (bubbleEl) bubbleEl.style.display = '';
  positionBubble();
}

// hideBubble() removed: the bubble now stays up for the life of the screen, one
// line replacing the next. stopPresenter() removes the element outright.

function positionBubble() {
  if (!bubbleEl || !containerEl) return;
  const rect = containerEl.getBoundingClientRect();

  // Always directly below the presenter, never to the side. The --bottom
  // variant places the tail on the bubble's top edge at left: 50%, so it points
  // up and stays centred on the bubble regardless of how long the copy is.
  //
  // Horizontally the bubble is centred on the viewport rather than on the
  // presenter: it used to be anchored to her and cycled through four sides,
  // which pushed long lines off the edge of a phone screen.
  bubbleEl.className = 'kassie-loader-v2__bubble kassie-loader-v2__bubble--bottom';
  bubbleEl.style.position = 'fixed';
  bubbleEl.style.top = `${rect.bottom + 8}px`;
  bubbleEl.style.left = '50%';
  bubbleEl.style.right = 'auto';
  bubbleEl.style.bottom = 'auto';
  bubbleEl.style.transform = 'translateX(-50%)';
  bubbleEl.style.zIndex = '10000';
}

/**
 * Force the speech bubble to re-measure and reposition itself.
 *
 * positionBubble() only ever ran as a side effect of setBubbleText() (a new
 * line appearing) or the initial setup — nothing re-ran it when the
 * surrounding layout changed for a different reason. The presenter sits
 * above the activation card in a centered flex column, so switching between
 * activation states (e.g. "Use a code" swapping in the taller code-entry
 * panel) changes the card's height and re-centers that whole column,
 * physically moving the avatar — while the bubble, positioned from a
 * getBoundingClientRect() snapshot taken before that reflow, stayed put
 * until the next idle line happened to roll around (up to several seconds
 * later). Exported so callers that trigger a layout change (like
 * DeviceActivation.js's showState()) can ask the bubble to catch up
 * immediately. Safe to call even if the presenter isn't active.
 */
export function repositionBubble() {
  // Wait one frame so the DOM's display/height changes have actually been
  // painted before measuring — calling this synchronously in the same tick
  // as the display toggle would still read the pre-change layout.
  requestAnimationFrame(() => {
    if (bubbleEl && bubbleEl.style.display !== 'none') {
      positionBubble();
    }
  });
}

/**
 * Stop presenting (called when navigating away from activation)
 */
export function stopPresenter() {
  presenting = false;
  document.removeEventListener('mousemove', handlePresenterMouseMove);
  window.removeEventListener('scroll', handlePresenterScroll, { capture: true });
  if (scrollRepositionFrame !== null) {
    cancelAnimationFrame(scrollRepositionFrame);
    scrollRepositionFrame = null;
  }
  clearTimeout(presenterLookAroundTimer);
  clearTimeout(earWiggleTimer);
  clearTimeout(winkTimer);
  // Clean up dizzy if active
  if (presenterDizzyActive) endPresenterDizzy();
  if (character) {
    character.stopBlinking();
    character.stopMouthCycle();
    character.destroy();
    character = null;
  }
  if (containerEl) {
    containerEl.remove();
    containerEl = null;
  }
  if (bubbleEl) {
    bubbleEl.remove();
    bubbleEl = null;
  }
  initialized = false;
}
